import { UseInfiniteQueryResult } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

export interface UseFetchAllPagesArgs<
  TData,
  TError,
  Q extends Pick<
    UseInfiniteQueryResult<TData, TError>,
    "fetchNextPage" | "hasNextPage" | "isLoading" | "isFetched" | "isError" | "isFetchingNextPage"
  >,
> {
  infiniteQuery: Q;
  enabled?: boolean;
  maxFetches?: number;
}

export const useFetchAllPages = <
  TData,
  TError,
  Q extends Pick<
    UseInfiniteQueryResult<TData, TError>,
    "fetchNextPage" | "hasNextPage" | "isLoading" | "isFetched" | "isError" | "isFetchingNextPage"
  >,
>({
  infiniteQuery: { fetchNextPage, hasNextPage, isLoading, isFetched, isError, isFetchingNextPage },
  enabled = true,
  maxFetches = 5,
}: UseFetchAllPagesArgs<TData, TError, Q>) => {
  const fetchCountRef = useRef(0);
  const hasErrorRef = useRef(false);

  useEffect(() => {
    if (!enabled || isLoading || !isFetched || isError || isFetchingNextPage) return;
    if (hasErrorRef.current || fetchCountRef.current >= maxFetches) return;
    if (hasNextPage) {
      fetchNextPage().then((query) => {
        if (query.status === "error") {
          hasErrorRef.current = true;
        } else {
          fetchCountRef.current += 1;
        }
      });
    }
  }, [enabled, fetchNextPage, hasNextPage, isError, isFetched, isLoading, isFetchingNextPage, maxFetches]);
};
